/**
 * Color Generation Utilities
 * Uses HSL manipulation to derive harmonious secondary colors from a primary brand color
 */

interface RGB {
  r: number;
  g: number;
  b: number;
}

interface HSL {
  h: number;
  s: number;
  l: number;
}

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex: string): RGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return { r: 0, g: 0, b: 0 };
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

/**
 * Convert hex color to HSL
 */
function hexToHsl(hex: string): HSL {
  const rgb = hexToRgb(hex);
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HSL to hex color
 */
function hslToHex(hsl: HSL): string {
  const h = hsl.h / 360;
  const s = hsl.s / 100;
  const l = hsl.l / 100;

  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x: number): string => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Clamp a value between min and max
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Generate a full primary color palette (50-900) from a base color
 * Uses the base color as the 600 shade (standard for primary buttons)
 */
export function generatePrimaryPalette(baseColor: string): Record<string, string> {
  const hsl = hexToHsl(baseColor);

  // Lightness values for each shade (designed to create a harmonious palette)
  const lightnessMap: Record<number, number> = {
    50: 97,
    100: 94,
    200: 86,
    300: 76,
    400: 64,
    500: 52,
    600: hsl.l, // Keep the original lightness for 600
    700: clamp(hsl.l - 12, 15, 45),
    800: clamp(hsl.l - 20, 10, 35),
    900: clamp(hsl.l - 28, 5, 25),
  };

  const shades: Record<string, string> = {};

  for (const [shade, lightness] of Object.entries(lightnessMap)) {
    // Slightly increase saturation for lighter shades to maintain vibrancy
    const satAdjust = Number(shade) < 500 ? Math.min(hsl.s + 5, 100) : hsl.s;
    shades[`primary_${shade}`] = hslToHex({
      h: hsl.h,
      s: satAdjust,
      l: lightness,
    });
  }

  return shades;
}

/**
 * Generate action colors (always warm amber/orange for CTAs)
 * These stay in the warm spectrum regardless of primary color
 * because warm colors are universally effective for CTAs
 */
export function generateActionColors(): Record<string, string> {
  return {
    action_light: '#fbbf24', // Amber-400
    action: '#f59e0b', // Amber-500
    action_dark: '#ea580c', // Orange-600
  };
}

/**
 * Generate tier/gamification colors
 * These are kept as universal recognizable colors
 */
export function generateTierColors(): Record<string, string> {
  return {
    tier_bronze: '#d97706', // Amber-600
    tier_gold: '#eab308', // Yellow-500
    tier_super: '#f59e0b', // Amber-500
    star_rating: '#facc15', // Yellow-400
  };
}

/**
 * Generate accent color from primary (analogous color harmony)
 */
export function generateAccentColor(primaryColor: string): Record<string, string> {
  const hsl = hexToHsl(primaryColor);

  // Shift hue by ~35-45 degrees for an analogous but distinct accent
  const accentHue = (hsl.h + 40) % 360;

  return {
    accent: hslToHex({
      h: accentHue,
      s: clamp(hsl.s + 10, 40, 100),
      l: 50,
    }),
    accent_dark: hslToHex({
      h: accentHue,
      s: clamp(hsl.s + 10, 40, 100),
      l: 40,
    }),
  };
}

/**
 * Generate a full theme from a single brand color
 * Action colors remain warm, other colors are derived from brand color
 */
export function generateFullTheme(brandColor: string): Record<string, string> {
  return {
    ...generatePrimaryPalette(brandColor),
    ...generateAccentColor(brandColor),
    ...generateActionColors(),
    ...generateTierColors(),
  };
}

/**
 * Validate if a string is a valid hex color
 */
export function isValidHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}

// ============================================================================
// Color Harmony Types & Accent Options
// ============================================================================

export type HarmonyType = 'complementary' | 'analogous' | 'triadic' | 'split-complementary' | 'tetradic' | 'monochromatic';

export interface AccentOption {
  type: HarmonyType;
  name: string;
  description: string;
  colors: { accent: string; accent_dark: string };
  angle: number; // Hue shift from primary (0 for monochromatic)
}

/**
 * Generate an accent color pair at a specific hue angle from primary
 */
function generateAccentAtAngle(primaryColor: string, angle: number): { accent: string; accent_dark: string } {
  const hsl = hexToHsl(primaryColor);
  const accentHue = (hsl.h + angle + 360) % 360;

  return {
    accent: hslToHex({
      h: accentHue,
      s: clamp(hsl.s + 10, 40, 100),
      l: 50,
    }),
    accent_dark: hslToHex({
      h: accentHue,
      s: clamp(hsl.s + 10, 40, 100),
      l: 40,
    }),
  };
}

/**
 * Generate monochromatic accent (same hue, different shade)
 */
function generateMonochromaticAccent(primaryColor: string): { accent: string; accent_dark: string } {
  const hsl = hexToHsl(primaryColor);

  return {
    accent: hslToHex({
      h: hsl.h,
      s: clamp(hsl.s - 10, 30, 90),
      l: clamp(hsl.l + 15, 40, 70),
    }),
    accent_dark: hslToHex({
      h: hsl.h,
      s: clamp(hsl.s - 5, 35, 95),
      l: clamp(hsl.l - 10, 25, 50),
    }),
  };
}

/**
 * Generate multiple accent color options based on color theory harmonies
 * Returns 6 options for user to choose from
 */
export function generateAccentOptions(primaryColor: string): AccentOption[] {
  return [
    {
      type: 'complementary',
      name: 'Complementary',
      description: 'Colors directly opposite on the color wheel. Creates high contrast and energy. Best for bold, attention-grabbing accents.',
      colors: generateAccentAtAngle(primaryColor, 180),
      angle: 180,
    },
    {
      type: 'analogous',
      name: 'Analogous',
      description: 'Neighboring colors on the wheel. Creates a harmonious, cohesive feel. Best for calm, unified appearance.',
      colors: generateAccentAtAngle(primaryColor, 30),
      angle: 30,
    },
    {
      type: 'triadic',
      name: 'Triadic',
      description: 'Three colors equally spaced on the wheel. Provides vibrant variety while maintaining balance. Best for playful, dynamic brands.',
      colors: generateAccentAtAngle(primaryColor, 120),
      angle: 120,
    },
    {
      type: 'split-complementary',
      name: 'Split-Complementary',
      description: 'Adjacent to the complement. Offers contrast without the intensity of pure complementary. Best for sophisticated designs.',
      colors: generateAccentAtAngle(primaryColor, 150),
      angle: 150,
    },
    {
      type: 'tetradic',
      name: 'Tetradic (Square)',
      description: 'Four colors equally spaced on the wheel. Provides rich variety and visual interest. Best for diverse, multi-colored palettes.',
      colors: generateAccentAtAngle(primaryColor, 90),
      angle: 90,
    },
    {
      type: 'monochromatic',
      name: 'Monochromatic',
      description: 'Variations of your primary color in different shades. Creates an elegant, unified look. Best for minimalist, professional brands.',
      colors: generateMonochromaticAccent(primaryColor),
      angle: 0,
    },
  ];
}

/**
 * Generate a full theme using the user's selected accent option
 */
export function generateThemeWithAccent(
  primaryColor: string,
  accentOption: AccentOption
): Record<string, string> {
  return {
    ...generatePrimaryPalette(primaryColor),
    accent: accentOption.colors.accent,
    accent_dark: accentOption.colors.accent_dark,
    ...generateActionColors(),
    ...generateTierColors(),
  };
}
